<template>
  <!-- On the design system: PrimeVue's own dialog, with the container, header, scrolling body and foot of the
       preset (src/ui/preset.js), over the flat scrim. -->
  <PDialog
    v-if="card"
    class="bz-dialog bz-dialog--card"
    :visible="visible"
    position="center"
    modal
    :auto-z-index="false"
    :draggable="false"
    :closable="false"
    :dismissable-mask="false"
    :close-on-escape="false"
    :aria-labelledby="titleId"
    :pt="{ mask: { class: 'bz-dialog-mask bz-dialog-mask--card', style: { zIndex: z }, 'data-testid': maskTestid, onPointerdown: onMaskDown, onClick: onMaskClick } }"
  >
    <template #header>
      <div class="bz-dialog__heading">
        <p v-if="eyebrow" class="bz-dialog__eyebrow">{{ eyebrow }}</p>
        <h2 :id="titleId" class="bz-dialog__title" :data-testid="titleTestid">{{ title }}</h2>
        <p v-if="subtitle" class="bz-dialog__subtitle">{{ subtitle }}</p>
      </div>
      <BzButton
        v-if="closeLabel"
        kind="secondary"
        icon="close"
        :aria-label="closeLabel"
        :data-testid="closeTestid"
        @click="$emit('dismiss', 'close')"
      />
    </template>
    <slot />
    <template v-if="$slots.footer" #footer><slot name="footer" /></template>
  </PDialog>
  <!-- Not yet on the system (the manual order, the help flow, the changelog): the frame only, around the
       component's own overlay and card. -->
  <PDialog
    v-else
    class="bz-dialog bz-dialog--frame"
    :visible="frameOpen"
    modal
    :auto-z-index="false"
    :draggable="false"
    :closable="false"
    :dismissable-mask="false"
    :close-on-escape="false"
    :aria-labelledby="labelledBy"
    :pt="{ mask: { class: 'bz-dialog-mask bz-dialog-mask--frame', style: { zIndex: z, pointerEvents: 'none' } } }"
  >
    <template #container>
      <transition :name="transition" appear @after-leave="onAfterLeave">
        <slot v-if="visible" />
      </transition>
    </template>
  </PDialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import { openLayer } from '@/ui/backClose';
import { Z } from '@/ui/layers';
import BzButton from './BzButton.vue';

let next = 0;

/**
 * The dialogs open, in the order they opened. While any is, <body> carries `bz-dialog-open` (see
 * App.vue's style); and only the one drawn on top answers Escape.
 */
const openDialogs = [];
function track(dialog, isOpen) {
    const at = openDialogs.indexOf(dialog);
    if (isOpen && at < 0) openDialogs.push(dialog);
    if (!isOpen && at >= 0) openDialogs.splice(at, 1);
    document.body.classList.toggle('bz-dialog-open', openDialogs.length > 0);
}

/** How many dialogs are open. For tests. */
export function openDialogCount() {
    return openDialogs.length;
}

/**
 * A dialog: PrimeVue's Dialog — the portal to <body>, the focus trap, `role="dialog"` with
 * `aria-modal`, Android's back — in one of two ways.
 *
 * `card` (the design system): the library draws the dialog, as both systems' alerts — a container of
 * round corners, centred on any screen, its header (an eyebrow, the `title`, a `subtitle`, and a quiet X
 * when there is a `closeLabel`), the body in the default slot, which scrolls when it does not fit, and the
 * `footer` slot, over a flat scrim that takes the taps outside. Centred on a phone too: a sheet from the
 * bottom is how both systems present a menu, not a question (feedback 2-oct-2026).
 *
 * @rule   A tap on the scrim closes a card, as its X does (Adrián, 13-oct-2026: «al pulsar fuera de un modal
 *         se tiene que cerrar»; it was off since 8-oct), and never leaks through: under the scrim is the
 *         shutter, so the scrim keeps the tap. Only a press that began AND ended on the scrim counts: a finger
 *         that starts on the card and lifts outside it (selecting, scrolling the body) closes nothing. Not
 *         PrimeVue's `dismissableMask`, which closes by itself: the parent decides, as for the X.
 * @rule   The X, back, Escape and the scrim come to the parent as `dismiss` with 'close', 'back', 'escape'
 *         or 'outside'.
 * @why    The library's own entrance (a scale and a fade, 300 ms) stays: it is transform and opacity,
 *         which norm 1 allows, and it is what a PrimeVue dialog looks like.
 *
 * Without `card` (the dialogs not yet on the system): the frame only, around the component's own
 * overlay and card, which draw exactly what they drew before migration stage 5.
 *
 * @rule   The slot is the component's overlay element, whole: its backdrop, its taps outside, its
 *         transition. The frame draws nothing — PrimeVue's mask is transparent and lets taps
 *         through, its box has no background, border, shadow, transform or animation — so the
 *         component's scoped CSS and its `@click.self` keep working untouched.
 * @why    PrimeVue's `.p-dialog` carries `transform: scale(1)`, and a transformed ancestor makes a
 *         `position: fixed` child place itself against that ancestor instead of the screen; its
 *         mask ends its entrance animation on its own background (`forwards`); and its box zooms in
 *         from 0.93. Each of those, left on, is a visible change.
 * @rule   `band` is the dialog's rung of src/ui/layers.js, and the dialog paints at exactly that
 *         number (`autoZIndex: false`).
 * @why    PrimeVue's own stacking puts a dialog at "the last overlay open + 1" whatever its base: the
 *         blocking notification arriving over the manual-order modal (2000) would land at 2001,
 *         under the coverage banner, instead of 9999. And base + 1 on the blocking band tied with
 *         the rider toasts. A library Select opened INSIDE one of these would not see the dialog on
 *         PrimeVue's ladder and open under it; none of the four has one.
 * @rule   Every way out the frame sees comes to the parent as `dismiss` with its reason — 'back'
 *         (Android's), 'escape' (only with `escape`) — and the dialog closes only when the parent
 *         sets `visible` to false. A back the parent does not act on (the help flow, working) arms
 *         a new history entry, so the next back still reaches the dialog and not the app.
 * @rule   With a `transition`, the frame stays until the slot has finished leaving — unless the dialog
 *         opened again meanwhile: Vue then cuts the leave short and still calls `after-leave`, and
 *         closing the frame there would leave the new one open inside nothing (the review of stage 5).
 * @why    PrimeVue's `modal` also blocks the body's scroll (`p-overflow-hidden`), whatever
 *         `blockScroll` says: harmless, as the document never scrolls and a tablet's scrollbar is 0 px.
 * @why    On close PrimeVue gives the focus back to what had it when the frame opened. A blocking
 *         notification that arrives while a cook types an order's code gives the keyboard back to
 *         that field when it closes — which is where the cook was, so it stays.
 * @rule   While a dialog is open, <body> carries `bz-dialog-open`, and App.vue hides Intercom's
 *         notification stack under it.
 * @why    The stack sits at the right, 148 px up on a tablet, above everything, and swallows the taps
 *         on what it covers (the v4.46.2 incident). The help flow's last tile falls under it, measured
 *         by tests/e2e/dialogs.spec.js; a dialog is the kitchen's full attention, and the previews
 *         come back the moment it closes.
 */
export default {
  name: 'BzDialog',
  components: { PDialog: Dialog, BzButton },
  props: {
    visible: { type: Boolean, required: true },
    band: { type: String, required: true, validator: (name) => name in Z },
    labelledBy: { type: String, default: undefined },
    transition: { type: String, default: undefined },
    escape: { type: Boolean, default: true },
    // The design system's dialog, and what its header says.
    card: { type: Boolean, default: false },
    title: { type: String, default: undefined },
    eyebrow: { type: String, default: undefined },
    subtitle: { type: String, default: undefined },
    closeLabel: { type: String, default: undefined },
    // Test hooks of a card: its scrim, its title and its X.
    maskTestid: { type: String, default: undefined },
    titleTestid: { type: String, default: undefined },
    closeTestid: { type: String, default: undefined },
  },
  emits: ['dismiss'],
  data() {
    return { frameOpen: this.visible, closeLayer: null, titleId: `bz-dialog-${++next}-title`, pressedOnMask: false };
  },
  computed: {
    z() {
      return Z[this.band];
    },
  },
  watch: {
    visible(open) {
      if (open) {
        this.frameOpen = true;
        this.arm();
      } else {
        this.disarm();
      }
    },
  },
  mounted() {
    if (this.visible) this.arm();
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    this.disarm();
  },
  methods: {
    onMaskDown(event) {
      this.pressedOnMask = event.target === event.currentTarget;
    },
    onMaskClick(event) {
      const outside = this.pressedOnMask && event.target === event.currentTarget;
      this.pressedOnMask = false;
      if (outside && this.visible) this.$emit('dismiss', 'outside');
    },
    arm() {
      track(this, true);
      if (this.closeLayer) return;
      this.closeLayer = openLayer(() => {
        this.closeLayer = null;
        this.$emit('dismiss', 'back');
        // Not closed by the parent: the entry back consumed is armed again.
        this.$nextTick(() => {
          if (this.visible) this.arm();
        });
      });
    },
    disarm() {
      const closed = this.closeLayer;
      this.closeLayer = null;
      if (closed) closed();
      track(this, false);
    },
    onAfterLeave() {
      if (!this.visible) this.frameOpen = false;
    },
    onKeydown(event) {
      if (!this.escape || !this.visible || event.key !== 'Escape' || event.isComposing) return;
      // The one on top is the one drawn on top: the highest band, and of those the last opened.
      const top = openDialogs.reduce((a, d) => (d.z >= a.z ? d : a));
      if (top !== this) return;
      this.$emit('dismiss', 'escape');
    },
  },
};
</script>

<style>
/* The frame is invisible and lets every tap through to the overlay in the slot. And it passes on
   what <body> passes on, as the overlay's old parent did: PrimeVue's mask and box set their own
   text colour and a 1.5 line height, and every card grew by it (the changelog, 11.5 px, measured). */
.bz-dialog-mask--frame.p-overlay-mask {
  --px-mask-background: transparent;
  background: transparent;
  animation: none;
  color: inherit;
  line-height: inherit;
}

.bz-dialog--frame.p-dialog {
  pointer-events: auto;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  transform: none;
  will-change: auto;
  max-height: none;
  color: inherit;
  line-height: inherit;
}

.bz-dialog--frame.p-dialog-enter-active,
.bz-dialog--frame.p-dialog-leave-active {
  animation: none;
}

/* The design system's dialog. Its container, padding and type are the preset's; what is here is what
   the preset has no token for. */

/* The scrim appears as it is, flat: PrimeVue fades it in by animating `background`, a repaint of the
   whole screen for 300 ms over the camera (norm 1). The container keeps its own entrance (transform,
   opacity). */
.bz-dialog-mask--card.p-overlay-mask {
  animation: none;
}

/* Its entrance: PrimeVue's (a scale from 0.93 and a fade: transform and opacity), decelerating without a
   bounce. */
.bz-dialog--card.p-dialog-enter-active {
  animation-duration: var(--bz-duration);
  animation-timing-function: var(--bz-ease-out);
}

/* And it leaves faster than it came. */
.bz-dialog--card.p-dialog-leave-active {
  animation-duration: var(--bz-duration-fast);
}

@media (prefers-reduced-motion: reduce) {
  .bz-dialog--card.p-dialog-enter-active,
  .bz-dialog--card.p-dialog-leave-active {
    animation: none;
  }
}

.bz-dialog--card.p-dialog {
  width: min(var(--bz-dialog-width), calc(100vw - 2 * var(--bz-space-4)));
  max-height: calc(100% - 2 * var(--bz-space-4));
  font-family: var(--bz-font);
  font-optical-sizing: auto;
  font-synthesis: none;
  font-kerning: normal;
  color-scheme: dark;
}

.bz-dialog--card .p-dialog-header {
  align-items: flex-start;
}

.bz-dialog__heading {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--bz-space-1);
}

.bz-dialog__eyebrow,
.bz-dialog__title,
.bz-dialog__subtitle {
  margin: 0;
}

.bz-dialog__eyebrow {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}

/* 22/28, the size both systems give a dialog's or a small bar's title (iOS's Title 2, Material 3's
   title-large), at 600. */
.bz-dialog__title {
  font-family: var(--bz-font-display);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  letter-spacing: var(--bz-tracking-xl);
  font-weight: var(--bz-weight-label);
  color: var(--bz-ink);
  overflow-wrap: anywhere;
}

.bz-dialog__subtitle {
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  letter-spacing: var(--bz-tracking-md);
  font-weight: var(--bz-weight-body);
  color: var(--bz-ink-2);
}

/* The X sits level with the title, quiet: no fill, the second ink. The content is the dialog's star, not
   its chrome; the target stays 48 px. */
.bz-dialog--card .p-dialog-header > .bz-button {
  flex: 0 0 auto;
  margin: calc(var(--bz-space-2) * -1) calc(var(--bz-space-3) * -1) 0 0;
  background: transparent;
  color: var(--bz-ink-2);
}

.bz-dialog--card .p-dialog-footer {
  display: block;
}
</style>
